/**
 * Admin · Einstellungen · Cron Jobs (23.09.2026)
 *
 * Die Wecker des Watchdog: Nachtlauf, Wächter, Morgen-Report. WAS ein Job tut,
 * steht im Code (server/services/cronJobCatalog.ts); WANN er läuft und OB er
 * pausiert ist, weiß Cloud Scheduler der Umgebung, in der die Session gerade
 * arbeitet. Diese Seite zeigt beides zusammen und schreibt nur nach GCP.
 *
 * Je Job: Anlegen (solange er in GCP fehlt), Zeitplan und Zeitzone speichern,
 * Pausieren/Fortsetzen, Jetzt ausführen, Ziel aktualisieren (wenn URL, Ausweis
 * oder Body in GCP vom Code abweichen — etwa nach einer neuen run.app-URL).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Alert, AlertTitle, Button, Chip, CircularProgress, Snackbar, TextField, MenuItem,
  Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { api } from '../../../api/client.js';
import { colors } from '../../../styles/theme.js';
import { describeSchedule, SCHEDULE_TIME_ZONES } from '../../../../shared/cronSchedule.js';
import type {
  CronJobKey, CronJobMutationResponse, CronJobView, CronJobsConfigView, CronJobsResponse,
} from '../../../../shared/cronJobTypes.js';

/** Die gängigen Zeitpläne als Vorlage — freie Cron-Ausdrücke bleiben möglich. */
const PRESETS: { label: string; value: string }[] = [
  { label: 'Täglich 01:00', value: '0 1 * * *' },
  { label: 'Täglich 07:00', value: '0 7 * * *' },
  { label: 'Werktags 01:00', value: '0 1 * * 1-5' },
  { label: 'Alle 10 Minuten', value: '*/10 * * * *' },
  { label: 'Alle 30 Minuten', value: '*/30 * * * *' },
  { label: 'Stündlich', value: '0 * * * *' },
];

type Action = 'create' | 'save' | 'pause' | 'resume' | 'run' | 'sync';

interface Draft { schedule: string; time_zone: string }

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StateChip({ job }: { job: CronJobView }) {
  if (!job.exists) return <Chip size="small" label="nicht angelegt" variant="outlined" sx={{ color: colors.black60 }} />;
  switch (job.state) {
    case 'ENABLED':
      return <Chip size="small" label="aktiv" sx={{ bgcolor: colors.lime, color: colors.black, fontWeight: 600 }} />;
    case 'PAUSED':
      return <Chip size="small" label="pausiert" sx={{ bgcolor: colors.scoreYellow, color: colors.black, fontWeight: 600 }} />;
    case 'UPDATE_FAILED':
      return <Chip size="small" label="Update fehlgeschlagen" sx={{ bgcolor: colors.scoreRed, color: colors.white }} />;
    case 'DISABLED':
      return <Chip size="small" label="deaktiviert (GCP)" sx={{ bgcolor: colors.silver }} />;
    default:
      return <Chip size="small" label={job.state ?? 'unbekannt'} variant="outlined" />;
  }
}

function LastRun({ job }: { job: CronJobView }) {
  if (!job.last_status) return <span>noch nie gelaufen</span>;
  const ok = job.last_status.code === 0;
  return (
    <span>
      {formatTime(job.last_attempt_at)}{' '}
      <Chip
        size="small"
        label={ok ? 'OK' : `Fehler ${job.last_status.code}`}
        sx={{ height: 18, fontSize: 11, bgcolor: ok ? colors.scoreGreen : colors.scoreRed, color: colors.white }}
      />
      {!ok && job.last_status.message && (
        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: colors.scoreRed }}>{job.last_status.message}</Typography>
      )}
    </span>
  );
}

/** Eine Zeile Projekt/Ziel/Ausweis — mit den Warnungen, die man VOR dem Anlegen sehen will. */
function ConfigPanel({ config }: { config: CronJobsConfigView }) {
  const row = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography variant="body2" sx={{ width: 150, flexShrink: 0, color: colors.black60 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0, wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  );
  const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 };
  const oidc = config.auth_mode === 'oidc';
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Cloud Scheduler dieser Umgebung</Typography>
      {row('Umgebung', <>Session <strong>{config.profile}</strong> → Jobs laufen mit <code>db_profile={config.job_profile}</code></>)}
      {row('Projekt · Region', <Box component="span" sx={mono}>{config.project || '— nicht konfiguriert —'} · {config.location}</Box>)}
      {row('Zugang', <Box component="span" sx={mono}>{config.credentials}</Box>)}
      {row('Ziel-URL', <Box component="span" sx={mono}>{config.target_base_url}</Box>)}
      {row('Ausweis', oidc
        ? <>OIDC-Token von <Box component="span" sx={mono}>{config.service_account}</Box></>
        : config.auth_mode === 'secret'
          ? 'Cron-Secret im Header (WATCHDOG_CRON_SECRET)'
          : 'keiner — weder Service-Account noch Cron-Secret gesetzt')}

      {config.target_is_load_balancer && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Die Ziel-URL ist kein <code>run.app</code>-Host. Hinter dem Load Balancer blockt Cloud Armor Googles Aufrufe —
          <code> WATCHDOG_SCHEDULER_TARGET_URL</code> auf die run.app-URL des Backends setzen.
        </Alert>
      )}
      {config.auth_mode === 'secret' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Die Jobs tragen das Cron-Secret als Header — es steht damit in der Job-Konfiguration in GCP. Sicherer ist
          ein Service-Account (<code>WATCHDOG_SCHEDULER_SERVICE_ACCOUNT</code>), dann weisen sich die Jobs per OIDC-Token aus.
        </Alert>
      )}
      {config.auth_mode === 'none' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Ohne <code>WATCHDOG_SCHEDULER_SERVICE_ACCOUNT</code> oder <code>WATCHDOG_CRON_SECRET</code> lässt sich kein Job anlegen —
          er könnte sich bei diesem Backend nicht ausweisen.
        </Alert>
      )}
      {oidc && config.service_account_allowlisted === false && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <code>{config.service_account}</code> steht nicht in <code>WATCHDOG_SERVICE_ACCOUNTS</code> — das Backend würde die Aufrufe mit 401 abweisen.
        </Alert>
      )}
      {oidc && config.audience_allowlisted === false && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Die Ziel-URL steht nicht in <code>WATCHDOG_SERVICE_AUDIENCES</code> — das Token der Jobs würde nicht akzeptiert.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" sx={{ color: colors.black60 }}>
        Zusätzlich läuft der Wächter im Prozess alle {config.sweeper.interval_minutes} Minuten
        (WATCHDOG_SWEEP_INTERVAL_MINUTES) — kein Scheduler-Job, nur solange eine Instanz wach ist.
        Hängend nach {config.sweeper.stale_minutes} min, nie gestartet nach {config.sweeper.stale_pending_hours} h,
        höchstens {config.sweeper.max_running} Scans gleichzeitig.
      </Typography>
    </Paper>
  );
}

export default function CronJobsSettingsPage() {
  const [data, setData] = useState<CronJobsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** `${key}:${action}` — genau ein Knopf dreht sich. */
  const [busy, setBusy] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [confirmRun, setConfirmRun] = useState<CronJobView | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<CronJobsResponse>('/admin/cron-jobs');
    setLoading(false);
    if (!res.success) { setError(res.error.message); return; }
    setData(res.data);
    setDrafts(Object.fromEntries(res.data.jobs.map((j) => [j.key, { schedule: j.schedule, time_zone: j.time_zone }])));
  }, []);

  useEffect(() => { void load(); }, [load]);

  const applyJob = (job: CronJobView) => {
    setData((d) => (d ? { ...d, jobs: d.jobs.map((j) => (j.key === job.key ? job : j)) } : d));
    setDrafts((x) => ({ ...x, [job.key]: { schedule: job.schedule, time_zone: job.time_zone } }));
  };

  const mutate = async (key: CronJobKey, action: Action, message: string) => {
    setBusy(`${key}:${action}`);
    setError(null);
    const draft = drafts[key];
    const res = action === 'create'
      ? await api.post<CronJobMutationResponse>(`/admin/cron-jobs/${key}`, draft)
      : action === 'save'
        ? await api.patch<CronJobMutationResponse>(`/admin/cron-jobs/${key}`, draft)
        : await api.post<CronJobMutationResponse>(`/admin/cron-jobs/${key}/${action}`, {});
    setBusy(null);
    if (!res.success) { setError(res.error.message); return; }
    applyJob(res.data.job);
    setToast(message);
  };

  const runNow = (job: CronJobView) => {
    // Der Nachtlauf scannt ALLE aktiven Domains — das will man nicht aus Versehen.
    if (job.key === 'night-run') setConfirmRun(job);
    else void mutate(job.key, 'run', `${job.title} angestoßen.`);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">Cron Jobs</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Neu laden">
          <IconButton size="small" onClick={() => { setLoading(true); void load(); }}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
        Die Wecker des Watchdog. Was ein Job tut, steht im Code — wann er läuft und ob er pausiert ist, in
        Cloud Scheduler. Änderungen hier gehen direkt nach GCP und landen im Audit-Log.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {data && <ConfigPanel config={data.config} />}

      {data?.listing_error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Cloud Scheduler nicht lesbar ({data.listing_error.code})</AlertTitle>
          {data.listing_error.message}
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            Die Jobs unten zeigen nur den Katalog — Zeitplan und Zustand aus GCP fehlen.
          </Typography>
        </Alert>
      )}

      {data?.jobs.map((job) => {
        const draft = drafts[job.key] ?? { schedule: job.schedule, time_zone: job.time_zone };
        const dirty = draft.schedule !== job.schedule || draft.time_zone !== job.time_zone;
        const isBusy = (action: Action) => busy === `${job.key}:${action}`;
        const anyBusy = busy !== null;
        const paused = job.state === 'PAUSED';
        const readOnly = data.listing_error !== null;

        return (
          <Paper key={job.key} elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{job.title}</Typography>
              <StateChip job={job} />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" sx={{ color: colors.black60, fontFamily: "'JetBrains Mono', monospace" }}>{job.job_id}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: colors.black60, mb: 2 }}>{job.description}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1.5 }}>
              <TextField
                label="Zeitplan (Cron)" size="small" value={draft.schedule}
                onChange={(e) => setDrafts((x) => ({ ...x, [job.key]: { ...draft, schedule: e.target.value } }))}
                disabled={readOnly}
                sx={{ width: 200, '& input': { fontFamily: "'JetBrains Mono', monospace" } }}
                helperText={describeSchedule(draft.schedule)}
              />
              <TextField
                select label="Vorlage" size="small" value="" disabled={readOnly}
                onChange={(e) => setDrafts((x) => ({ ...x, [job.key]: { ...draft, schedule: e.target.value } }))}
                sx={{ width: 190 }}
                helperText={`Vorgabe: ${job.default_schedule}`}
              >
                {PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label} <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.black60 }}>{p.value}</Typography>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Zeitzone" size="small" value={draft.time_zone} disabled={readOnly}
                onChange={(e) => setDrafts((x) => ({ ...x, [job.key]: { ...draft, time_zone: e.target.value } }))}
                sx={{ width: 170 }}
              >
                {SCHEDULE_TIME_ZONES.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
              </TextField>
              {job.exists && (
                <Button
                  variant="contained" size="small" startIcon={isBusy('save') ? <CircularProgress size={14} /> : <SaveIcon />}
                  disabled={!dirty || anyBusy || readOnly}
                  onClick={() => void mutate(job.key, 'save', 'Zeitplan gespeichert.')}
                  sx={{ mt: 0.5, bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey } }}
                >
                  Zeitplan speichern
                </Button>
              )}
            </Box>

            <Typography variant="body2" component="div" sx={{ color: colors.black60, mb: 0.5 }}>
              Nächster Lauf: <strong>{job.exists && !paused ? formatTime(job.next_run_at) : '—'}</strong>
              {' · '}Letzter Lauf: <LastRun job={job} />
            </Typography>
            <Typography variant="caption" component="div" sx={{ color: colors.black60, fontFamily: "'JetBrains Mono', monospace", mb: 1.5, wordBreak: 'break-all' }}>
              Ziel: {job.target_uri ?? job.expected_uri}
              {job.auth_mode ? ` · Ausweis: ${job.auth_mode === 'oidc' ? 'OIDC-Token' : job.auth_mode === 'secret' ? 'Cron-Secret' : 'keiner'}` : ''}
              {job.attempt_deadline_seconds ? ` · Deadline ${job.attempt_deadline_seconds} s` : ''}
            </Typography>

            {job.exists && !job.target_matches && (
              <Alert
                severity="warning" sx={{ mb: 1.5 }}
                action={(
                  <Button
                    size="small" color="inherit" startIcon={isBusy('sync') ? <CircularProgress size={14} /> : <SyncIcon />}
                    disabled={anyBusy} onClick={() => void mutate(job.key, 'sync', 'Ziel in GCP aktualisiert.')}
                  >
                    Ziel aktualisieren
                  </Button>
                )}
              >
                Der Job in GCP weicht vom Code ab (Ziel-URL, Ausweis oder Body). Erwartet: <code>{job.expected_uri}</code>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!job.exists ? (
                <Button
                  variant="contained" size="small" startIcon={isBusy('create') ? <CircularProgress size={14} /> : <AddCircleOutlineIcon />}
                  disabled={anyBusy || readOnly || data.config.auth_mode === 'none'}
                  onClick={() => void mutate(job.key, 'create', `${job.title} in Cloud Scheduler angelegt.`)}
                  sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey } }}
                >
                  In Cloud Scheduler anlegen
                </Button>
              ) : (
                <>
                  {paused ? (
                    <Button
                      variant="outlined" size="small" startIcon={isBusy('resume') ? <CircularProgress size={14} /> : <PlayCircleOutlineIcon />}
                      disabled={anyBusy} onClick={() => void mutate(job.key, 'resume', `${job.title} fortgesetzt.`)}
                    >
                      Fortsetzen
                    </Button>
                  ) : (
                    <Button
                      variant="outlined" size="small" startIcon={isBusy('pause') ? <CircularProgress size={14} /> : <PauseCircleOutlineIcon />}
                      disabled={anyBusy} onClick={() => void mutate(job.key, 'pause', `${job.title} pausiert.`)}
                    >
                      Pausieren
                    </Button>
                  )}
                  <Button
                    variant="outlined" size="small" startIcon={isBusy('run') ? <CircularProgress size={14} /> : <BoltIcon />}
                    disabled={anyBusy} onClick={() => runNow(job)}
                  >
                    Jetzt ausführen
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        );
      })}

      <Dialog open={confirmRun !== null} onClose={() => setConfirmRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nachtlauf jetzt starten?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Der Nachtlauf legt für <strong>jede aktive Domain</strong> einen Scan-Job an und stößt ihn an — mit
              db_profile <strong>{data?.config.job_profile}</strong>. Domains mit offenem Job werden übersprungen.
            </Typography>
            <Typography variant="body2">Das dauert je Domain 90–120 Sekunden und schreibt Rohzeilen nach BigQuery.</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRun(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => { const job = confirmRun; setConfirmRun(null); if (job) void mutate(job.key, 'run', `${job.title} angestoßen.`); }}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}
          >
            Ja, jetzt starten
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast !== null} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
